// Bottom tab bar: Kelder (cellar) / Proeven (tasting journal, the app's one primary "Proef een
// wijn" CTA lives on that tab's own screen) / Ontdekken (learning) / Samen (friends, groups, the
// inbox, the profile others see and, in its settings drawer, what the About tab used to hold). On
// a wide screen the same bar becomes a left rail, so a laptop never shows a phone's chrome
// stretched across the window.
import "./config";
import { useEffect, useRef, useState } from "react";
import { styled } from "tamagui";
import { navigate, useRoute } from "../lib/router";
import { useT } from "../lib/i18n";
import { startInboxBadgeWatch, useInboxBadge } from "../lib/social/inboxBadge";
import { updateOnMainSwitch } from "../lib/updates";
import { GestureArea } from "./gestures/GestureArea";
import { Pressable } from "./controls";
import { Icon, type IconName } from "./Icon";
import { Row, VisuallyHidden } from "./layout";
import { Badge } from "./surfaces";
import { Caption } from "./text";

type TabPath = "/cellar" | "/taste" | "/learn" | "/samen";

const TABS: ReadonlyArray<{
  path: TabPath;
  key: "tab.cellar.label" | "tab.taste.label" | "tab.learn.label" | "tab.samen.label";
  icon: IconName;
}> = [
  { path: "/cellar", key: "tab.cellar.label", icon: "cellar" },
  { path: "/taste", key: "tab.taste.label", icon: "tulip" },
  { path: "/learn", key: "tab.learn.label", icon: "compass" },
  { path: "/samen", key: "tab.samen.label", icon: "users" },
];

/** Routes that belong to Samen: the social screens, the profile, the settings and sign-in. */
const SAMEN_ROUTES = new Set([
  "samen",
  "about",
  "friends",
  "groups",
  "session",
  "invite",
  "signin",
]);

/** A screen reader hears a rising count at most this often, however fast invites arrive. */
const ANNOUNCE_EVERY_MS = 10_000;

/** Which tab a route belongs to: detail and flow screens light up the tab they were reached from,
 * so the bar never shows "nowhere" while the user is three screens deep into a tasting. */
export function activeTab(path: string): TabPath {
  const first = path.split("/").find((s) => s !== "") ?? "";
  if (first === "cellar") return "/cellar";
  if (first === "learn" || first === "profile") return "/learn";
  if (SAMEN_ROUTES.has(first)) return "/samen";
  return "/taste";
}

/** The polite announcement of a rising inbox count, throttled; a falling one is never read out. */
function useAnnouncement(count: number): string {
  const { t } = useT();
  const [message, setMessage] = useState("");
  const last = useRef({ count: 0, at: 0 });
  useEffect(() => {
    const now = Date.now();
    if (count > last.current.count && now - last.current.at >= ANNOUNCE_EVERY_MS) {
      setMessage(t("social.samen.announce", { count }));
      last.current.at = now;
    }
    last.current.count = count;
  }, [count, t]);
  return message;
}

const Bar = styled(Row, {
  name: "TabBar",
  render: "nav",
  position: "sticky",
  bottom: 0,
  zIndex: "1",
  gap: { default: "1", rail: "3" },
  alignItems: "stretch",
  paddingTop: { default: 6, rail: "5" },
  paddingHorizontal: { default: "2", rail: 10 },
  paddingBottom: { default: "calc(10px + env(safe-area-inset-bottom, 0px))", rail: "5" },
  borderTopColor: "line",
  flexDirection: { default: "row", rail: "column" },
  top: { default: "auto", rail: 0 },
  width: { default: "auto", rail: 96 },
  height: { default: "auto", rail: "100dvh" },
  borderTopWidth: { default: "var(--borderW)", rail: 0 },
  borderRightWidth: { default: 0, rail: "var(--borderW)" },
  borderRightColor: "line",
  backgroundColor: { default: "bg", rail: "bg2" },
  // Horizontal drags belong to the bar (swipe to the next tab); vertical ones still scroll the page.
  touchAction: "pan-y",
});

const Tab = styled(Pressable, {
  name: "Tab",
  flex: { default: 1, rail: 0 },
  gap: 3,
  minHeight: { default: 56, rail: 64 },
  paddingVertical: 4,
  borderRadius: "2",
});

export function TabBar(): React.JSX.Element {
  const { t } = useT();
  const route = useRoute();
  const routed = activeTab(route.path);
  const badge = useInboxBadge();
  const announcement = useAnnouncement(badge);
  // Optimistic: the tab lights on tap, before the (possibly heavy) screen has rendered.
  const [tapped, setTapped] = useState<TabPath | null>(null);
  useEffect(() => {
    setTapped(null);
  }, [route.path]);
  useEffect(() => startInboxBadgeWatch(), []);
  const active = tapped ?? routed;
  // Swiping the bar moves to the neighbouring tab; the tabs themselves are the tap and keyboard twin.
  // A waiting update is applied here, the one moment a reload is free: the person chose to leave
  // this screen, and the route they chose is already in the address bar for it to come back to.
  const goTo = (path: TabPath): void => {
    setTapped(path);
    navigate(path);
    if (path !== active) updateOnMainSwitch();
  };
  const step = (by: 1 | -1): void => {
    const next = TABS[TABS.findIndex((tab) => tab.path === active) + by];
    if (next !== undefined) goTo(next.path);
  };
  return (
    <GestureArea
      display="contents"
      axis="x"
      onSwipe={{ left: () => step(1), right: () => step(-1) }}
    >
      <Bar aria-label={t("app.meta.name")}>
        {TABS.map((tab) => {
          const isActive = active === tab.path;
          const count = tab.path === "/samen" ? badge : 0;
          const label = t(tab.key);
          return (
            <Tab
              key={tab.path}
              // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
              {...(isActive ? { "aria-current": "page" as const } : {})}
              // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
              {...(count > 0 ? { "aria-label": t("social.samen.badge", { label, count }) } : {})}
              onPress={() => {
                goTo(tab.path);
              }}
            >
              <Row
                position="relative"
                width={56}
                height={30}
                borderRadius="pill"
                justifyContent="center"
                backgroundColor={
                  isActive
                    ? { default: "accentSoft", a11yLight: "accent", a11yDark: "accent" }
                    : "transparent"
                }
                transitionProperty="background-color"
                transitionDuration="var(--durBase)"
              >
                <Icon
                  name={tab.icon}
                  size={22}
                  strokeWidth={isActive ? 1.9 : 1.6}
                  tone={isActive ? "accent" : "textFaint"}
                />
                {count > 0 && (
                  <Badge
                    position="absolute"
                    top={-2}
                    right={6}
                    minWidth={18}
                    height={18}
                    lineHeight={18}
                    paddingHorizontal={5}
                    textAlign="center"
                    fontVariant={["tabular-nums"]}
                    boxShadow="0 0 0 2px var(--bg)"
                    aria-hidden
                  >
                    {count > 99 ? "99+" : count}
                  </Badge>
                )}
              </Row>
              <Caption
                fontSize="1"
                fontWeight="700"
                color={isActive ? "accent" : "textFaint"}
                textDecorationLine={
                  isActive
                    ? { default: "none", a11yLight: "underline", a11yDark: "underline" }
                    : "none"
                }
              >
                {label}
              </Caption>
            </Tab>
          );
        })}
        <VisuallyHidden role="status">{announcement}</VisuallyHidden>
      </Bar>
    </GestureArea>
  );
}
